import React, { useState } from "react";
import { KTSVG } from "../../../helpers";

type Props = {
  className: string;
  innerPadding?: string;
};

const StepContent19: React.FC<Props> = ({ className, innerPadding = "" }) => {
  const [category, setCategory] = useState("1");
  const [option, setOption] = useState("1");
  const [fleet, setFleet] = useState("1");
  return (

      <div className={`card ${className}`}>
        {/* begin::Body */}
        <div className={`card-body ${innerPadding}`}>
          {/* begin::Title */}
          <div className="mb-10">
            <h3 className="fw-bold text-dark mb-2 fs-3">Calculate cost of customer acquisition (COCA)</h3>
            <span className="fs-7 text-muted">
             Read each section and then fill in your answers at the bottom.
            </span>
          </div>
          {/* end::Title */}

          {/* begin::Section */}
          <div className="mb-10">
            <div className="accordion" id="kt_accordion_1">
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_1">
                  <button
                    className="accordion-button fs-7 fw-bold"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_1"
                    aria-expanded="true"
                    aria-controls="kt_accordion_1_body_1"
                  >
                    Calculate cost of customer acquisition (COCA)
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_1"
                  className="accordion-collapse collapse show"
                  aria-labelledby="kt_accordion_1_header_1"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  In this step you will determine how much it costs to acquire a customer over the short term, medium term, and long term, based on your sales process. Entrepreneurs are highly optimistic, but this almost always blinds them to the real costs of customer acquisition. It is essential that you do realistic calculations and then make appropriate adjustments over time.

Caution: The Cost of Customer Acquisition (COCA) is an extremely important metric and can be difficult at first to understand and calculate. This step explains the COCA in detail, but you will need to pay close attention to the details to calculate it correctly. It requires a significant amount of effort and systematic thought. Do not skip or skim this step because getting COCA right is both critical and challenging.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_2">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_2"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_2"
                  >
                    Calculate COCA
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_2"
                  className="accordion-collapse collapse show"
                  aria-labelledby="kt_accordion_1_header_2"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  First-time entrepreneurs are always tempted to do a bottom-up calculation of COCA, however this method is not accurate. A completely accurate estimate of the cost to acquire one new customer is hard to project. There are situations where the real COCA is 10–20 times the bottom-up calculation. That’s why a top-down calculation will provide more realistic expectations.

Choose a period of time relevant for your business model, in which the customer will realize the value proposition from your product. If in doubt, use year 1, years 2 and 3, and years 4 and 5 as your three time periods.
Enlist an experienced person to help develop budget projections, and be sure to understand how adjusting costs affects the profitability of your business.
Calculate all your sales and marketing expenditures over a period of time. Be sure to include costs for all the key items in your sales and marketing plan: sales reps, auto, travel and entertainment, phone, Internet, demo units, technical sales support, website development, consultants, trade shows, real estate, administrative support, computers, and so on. Also calculate the cost in time that the executives on the team spend on sales as these are very real and expensive costs.
Tabulate your aggregate sales and marketing expenses (TMSE) over a period of time, and divide it by the period of time to get to TMSE(t).
If a sizeable portion of your TMSE(t) is the cost of retention of existing customers, rather than acquiring new customers, subtract this from the Substract retention cost from TMSE(t). We will refer to the cost of retention as the Install Base Support Expense over Time, or IBSE(t).
Determine the number of new customers you will close during each period of time (meaning delivering the product and collecting the money). This is NC(t)—New Customers over Time.
Calculate COCA for each of the three periods of time using the formula COCA(t)=(TMSE(t)–IBSE(t))/NC(t).
Plot the three values on a graph where the x-axis is time and the y-axis is COCA for that period. You can also draw a best-fit curve.

                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_3">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_3"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_3"
                  >
                    Consider ways to reduce COCA
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_3"
                  className="accordion-collapse collapse"
                  aria-labelledby="kt_accordion_1_header_3"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  COCA is dynamic, and will almost always start at a very high point (i.e., well above the final COCA and likely higher than the LTV) because you need to first create the market. Your organization will seek ways to reduce these costs to make the business much more attractive. Think about some of the common ways this can be done:

While very powerful, use direct sales judiciously as it is very expensive. As an alternative, consider investing instead in technological enablers, from telemarketing to having an effective web presence to engaging through social media in order to decrease costs as much as possible.
Automate as much as possible, even if it requires significant investments. If you can, promote your product through sites where there are big networks and opportunities to make your message go viral, or automate your marketing by creating incentive schemes for your users similar to Avon or Groupon.
Improve conversion rates in sales. As you see in the bottom-up calculations, there is a huge cost associated with chasing deals that you don’t close. Getting higher conversion rates on leads opens up the funnel so more deals get through, increasing your revenue and decreasing your COCA.
Decrease the cost of leads and improve the quality of leads by incorporating tools and techniques into your sales process that increase the quality of your leads, and paying attention to where your leads are coming from. This will improve your conversion rates.
Speed through the sales funnel. By focusing on the speed at which prospects are moving through the sales cycle, you can decrease the sales cycle, which will have a dramatic positive effect on reducing the COCA.
Choose your business model with COCA in mind. You can switch the business model (the value capturing part) during the life of your business—for example by switching from an usage-based model to a “cell phone” type of model, where customers paid a fixed amount each month for an agreed-upon type of service, with the flexibility to buy additional service on a usage basis. This can make sales cycle length decreased dramatically.
Word of mouth, which is the biggest driver of reducing COCA. This tends to dramatically decrease the sales cycle, decrease the customer’s desire to push you for discounts, and bring in well-qualified customers who already are good fits for the product, so salespeople can be much more productive in dealing with them.
Stay focused on the beachhead market from the earliest steps of this process, and do not get distracted by customers outside of your chosen market. This will help improve word of mouth and also make your sales reps much more productive.
                  </div>
                </div>
              </div>

            </div>
          </div>
          {/* end::Section */}

        </div>
        {/* end: Card Body */}
      </div>

  );
};

export { StepContent19 };
